import { useMemo, useState } from 'react';
import { cn, cx } from '@/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Controller, useFormContext } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';

import InfoBar from '@/components/common/Icons/InfoBar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/common/PopoverShadcn';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/SelectShadcn';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/Command';

export interface SelectOption {
  label: string;
  value: string;
}

type Props = {
  currentId: string;
  options: SelectOption[];
  title?: string;
  onChange?: () => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  infoBoolean?: boolean;
  infoText?: string;
  /** Habilita el campo de búsqueda para filtrar opciones */
  searchable?: boolean;
  /** Placeholder para el campo de búsqueda */
  searchPlaceholder?: string;
  /** Texto a mostrar cuando no hay resultados */
  emptyText?: string;
};

/**
 * Select component using Shadcn Select (Radix).
 * Compatible with Dialog modals - renders dropdown via portal.
 * Supports searchable mode via Popover + Command.
 */
const InputSelectShadcn = ({
  currentId,
  options,
  title,
  onChange,
  className,
  placeholder = 'Selecciona',
  disabled = false,
  infoBoolean = false,
  infoText = '',
  searchable = false,
  searchPlaceholder = 'Buscar...',
  emptyText = 'No se encontraron resultados.',
}: Props) => {
  const { control } = useFormContext();
  const [open, setOpen] = useState(false);

  const renderLabel = useMemo(() => {
    if (!title) return null;
    return (
      <label className="body-text1 flex items-center gap-2 text-tundora">
        {title}
        {infoBoolean && (
          <>
            <a
              data-tooltip-id={`tooltip-${currentId}`}
              data-tooltip-content={`${infoText}`}
              className="cursor-pointer"
            >
              <InfoBar />
            </a>
            <Tooltip
              className="font-workSans"
              style={{
                backgroundColor: 'white',
                cursor: 'pointer',
                color: 'black',
                borderRadius: '10px',
                height: '50px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
              }}
              id={`tooltip-${currentId}`}
            />
          </>
        )}
      </label>
    );
  }, [title, infoBoolean, currentId, infoText]);

  // Searchable mode using Popover + Command (Combobox pattern)
  if (searchable) {
    return (
      <div className={cx('flex-col', className)}>
        {renderLabel}
        <Controller
          control={control}
          name={currentId}
          render={({ field: { onChange: controllerChange, value } }) => {
            const selectedOption = options.find((opt) => opt.value === value);
            return (
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild disabled={disabled}>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                      'mt-2 flex h-10 w-full items-center justify-between rounded-md border-2 border-[#BABABA] bg-white px-3 py-2 text-sm hover:border-[#2563EB] focus:border-[#3B82F6] focus:outline-none',
                      disabled && 'cursor-not-allowed opacity-50',
                      !selectedOption && 'text-muted-foreground'
                    )}
                  >
                    <span className="truncate">
                      {selectedOption ? selectedOption.label : placeholder}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="z-popover-modal w-[--radix-popover-trigger-width] p-0">
                  <Command>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList>
                      <CommandEmpty>{emptyText}</CommandEmpty>
                      <CommandGroup>
                        {options.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.label}
                            onSelect={() => {
                              controllerChange(option.value);
                              onChange?.();
                              setOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                value === option.value ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            );
          }}
        />
      </div>
    );
  }

  // Default mode using Radix Select
  return (
    <div className={cx('flex-col', className)}>
      {renderLabel}
      <Controller
        control={control}
        name={currentId}
        render={({ field: { onChange: controllerChange, value } }) => (
          <Select
            value={value || ''}
            onValueChange={(newValue) => {
              controllerChange(newValue);
              onChange?.();
            }}
            disabled={disabled}
          >
            <SelectTrigger className="mt-2 h-10 rounded-md border-2 border-[#BABABA] bg-white hover:border-[#2563EB] focus:border-[#3B82F6]">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
    </div>
  );
};

export default InputSelectShadcn;
